import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getAuthPathToken,
  type IAuthPathTokenParams,
  type IAuthPathTokenData,
} from '@/pages/home/services/authPath';

export interface IBaseAuthMenu {
  id?: string | number;
  u_id?: string | number;
  page_type?: string;
  path?: string;
  auth_path?: string;
  name_en?: string;
}

export interface IBaseAuthGame {
  game_code?: string;
  studio_code?: string;
  game_name?: string;
}

export type TAuthPathErrorKey =
  | 'authpath_error_no_report'
  | 'authpath_error_no_game'
  | 'authpath_error_menu_mismatch'
  | 'authpath_error_no_url'
  | 'authpath_error_token_empty'
  | 'authpath_error_fetch_failed';

interface IAuthPathState {
  loading: boolean;
  errorKey: TAuthPathErrorKey | '';
  authUrl: string;
}

const INIT_STATE: IAuthPathState = {
  loading: false,
  errorKey: '',
  authUrl: '',
};

interface ICanOpenArgs<TMenu, TGame> {
  reportId?: string;
  menu: TMenu;
  game?: TGame | null;
}

interface ICanOpenResult {
  ok: boolean;
  errorKey?: TAuthPathErrorKey;
}

interface IBuildTokenParamsArgs<TMenu, TGame> {
  menu: TMenu;
  game?: TGame | null;
  authPath: string;
  menuId: string;
  pageType: string;
}

interface IUseAuthPathReportOptions<
  TMenu extends IBaseAuthMenu,
  TGame extends IBaseAuthGame,
> {
  reportId?: string;
  currentMenu?: TMenu | null;
  currentGame?: TGame | null;

  getAuthPath: (menu: TMenu) => string;
  getMenuId: (menu: TMenu) => string;
  getPageType: (menu: TMenu) => string;

  buildTokenParams: (
    args: IBuildTokenParamsArgs<TMenu, TGame>,
  ) => IAuthPathTokenParams;

  canOpen?: (args: ICanOpenArgs<TMenu, TGame>) => ICanOpenResult;
  onSuccess?: (args: { menu: TMenu; game?: TGame | null; authUrl: string; }) => void;
  fetchToken?: (params: IAuthPathTokenParams) => Promise<IBaseResponse<IAuthPathTokenData | null>>;
}

const buildAuthUrl = (authPath: string, token: string): string => {
  const connector = authPath.includes('?') ? '&' : '?';
  return `${authPath}${connector}token=${encodeURIComponent(token)}`;
};

const getMenuStableKeyFromMenu = (menu?: IBaseAuthMenu | null): string => {
  if (!menu) return 'NO_MENU';
  return String(menu.u_id || menu.id || menu.name_en || '');
};

const useLatestRef = <T,>(value: T) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

export function useAuthPathReport<
  TMenu extends IBaseAuthMenu,
  TGame extends IBaseAuthGame,
>(options: IUseAuthPathReportOptions<TMenu, TGame>) {
  const {
    reportId,
    currentMenu,
    currentGame,
    getAuthPath,
    getMenuId,
    getPageType,
    buildTokenParams,
    canOpen,
    onSuccess,
    fetchToken,
  } = options;

  const [state, setState] = useState<IAuthPathState>({...INIT_STATE, loading: true});
  const requestIdRef = useRef(0);
  const lastTriggerRef = useRef('');

  const latestRef = useLatestRef({
    reportId,
    currentMenu,
    currentGame,
    getAuthPath,
    getMenuId,
    getPageType,
    buildTokenParams,
    canOpen,
    onSuccess,
    fetchToken,
  });

  const triggerKey = useMemo(() => {
    return `${reportId || ''}__${getMenuStableKeyFromMenu(currentMenu)}`;
  }, [reportId, currentMenu]);

  const setSafeState = useCallback((payload: Partial<IAuthPathState>) => {
    setState({
      ...INIT_STATE,
      ...payload,
    });
  }, []);

  const run = useCallback(async () => {
    const {
      reportId: rid,
      currentMenu: menu,
      currentGame: game,
      getAuthPath: getPath,
      getMenuId: getId,
      getPageType: getType,
      buildTokenParams: buildParams,
      canOpen: canOpenFn,
      onSuccess: onSuccessFn,
      fetchToken: fetchTokenFn,
    } = latestRef.current;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (!menu) {
      setSafeState({ errorKey: 'authpath_error_menu_mismatch' });
      return;
    }

    if (canOpenFn) {
      const result = canOpenFn({ reportId: rid, menu, game });
      if (!result.ok) {
        setSafeState({ errorKey: result.errorKey || 'authpath_error_menu_mismatch' });
        return;
      }
    }

    const authPath = `${getPath(menu) || ''}`.trim();
    const menuId = `${getId(menu) || ''}`.trim();
    const pageType = `${getType(menu) || ''}`.trim();

    if (!authPath) {
      setSafeState({ errorKey: 'authpath_error_no_url' });
      return;
    }

    setSafeState({ loading: true });

    try {
      const params = buildParams({
        menu,
        game,
        authPath,
        menuId,
        pageType,
      });

      const tokenFetcher = fetchTokenFn || getAuthPathToken;
      const response = await tokenFetcher(params);

      if (requestId !== requestIdRef.current) return;

      const token = response?.data?.token;
      if (!token) {
        setSafeState({ errorKey: 'authpath_error_token_empty' });
        return;
      }

      const authUrl = buildAuthUrl(authPath, token);
      setSafeState({ authUrl });

      if (onSuccessFn) {
        onSuccessFn({ menu, game, authUrl });
      }
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setSafeState({ errorKey: 'authpath_error_fetch_failed' });
    }
  }, [latestRef, setSafeState]);

  useEffect(() => {
    if (lastTriggerRef.current === triggerKey) return;
    lastTriggerRef.current = triggerKey;
    run();
  }, [triggerKey, run]);

  const reload = useCallback(() => {
    lastTriggerRef.current = '';
    run();
  }, [run]);

  return {
    loading: state.loading,
    authUrl: state.authUrl,
    errorKey: state.errorKey,
    reload,
  };
}

export default useAuthPathReport;
